<template>
  <div class="table-responsive m-4 border border-2 rounded-3 shadow">
    <!-- Row mit 3 Spalten: links (Filter-Buttons), mittig (Suchfeld), rechts (Pagination) -->
    <div class="row align-items-center py-4 px-3">
      <div class="col-auto">
        <AppPagination
            :current-page="currentPage"
            :pageCount="pageCount"
            :maxCount="maxCount"
            @page-change="setPage"
            :max-invoices="maxInvoices"
            @set-max="setMax"
        />
      </div>
      <div class="col d-flex justify-content-center">
        <div class="input-group input-group-sm" style="height: 30px">
          <input
              type="text"
              class="form-control"
              v-model="searchTerm"
              :placeholder="$t('ui.search')"
              @keydown.enter.prevent="applyFilter"
              @update:modelValue="val => { searchTerm = val; }"
              @apply-filters="applyFilter"
          />
          <button class="btn btn-sm ehw-green" @click="applyFilter">
            <i class="bi bi-search"></i>
          </button>
        </div>
      </div>
      <div class="col-auto ps-0">
        <!--<button class="btn ehw-green btn-sm me-2" @click="applyFilter">{{ $t('ui.applyFilters') }}</button>-->
        <button class="btn ehw-green btn-sm" @click="clearFilter">{{ $t('ui.clearFilters') }}</button>
      </div>
    </div>

    <table class="table table-bordered table-hover">
      <thead class="thead-light">
      <tr>
        <th @click="changeSort('invoiceNumber')"
            class="cursor-pointer" style="min-width:220px"
        >
          {{ $t('table.invoiceNumber') }}
          <span class="float-end ms-2">
              <i :class="getSortIconClass('invoiceNumber')"></i>
            </span>
        </th>
        <th @click="changeSort('invoiceDate')" class="cursor-pointer" style="min-width:230px">
          {{ $t('table.invoiceDate') }}
          <span class="float-end ms-2">
              <i class="bi" :class="getSortIconClass('invoiceDate')"></i>
            </span>
        </th>
        <th @click="changeSort('creationTimestamp')" class="cursor-pointer" style="min-width:230px">
          {{ $t('table.creationDate') }}
          <span class="float-end ms-2">
              <i class="bi" :class="getSortIconClass('creationTimestamp')"></i>
            </span>
        </th>
        <th @click="changeSort('buyerName')" class="cursor-pointer">
          {{ $t('table.buyerName') }}
          <span class="float-end ms-2">
              <i class="bi" :class="getSortIconClass('buyerName')"></i>
            </span>
        </th>
        <th @click="changeSort('zugFeRDVersionId')" class="cursor-pointer" style="min-width:130px">
          {{ $t('table.zugferdVersion') }}
          <span class="float-end ms-2">
              <i class="bi" :class="getSortIconClass('zugFeRDVersionId')"></i>
            </span>
        </th>
        <th @click="changeSort('zugFeRDProfileId')" class="cursor-pointer">
          {{ $t('table.profile') }}
          <span class="float-end ms-2">
              <i class="bi" :class="getSortIconClass('zugFeRDProfileId')"></i>
            </span>
        </th>
        <th class="cursor-pointer">
          {{ $t('table.fileName') }}
          <span class="float-end ms-2"></span>
        </th>
      </tr>
      <!-- Filter row -->
      <tr>
        <th>
          <div class="input-group input-group-sm" @keydown.enter.prevent="applyFilter">
            <input
                type="text"
                class="form-control"
                style="line-height: 2"
                v-model="invoiceNumberFilter"
                @blur="applyFilter"
            >
            <button
                v-if="invoiceNumberFilter"
                class="btn btn-danger"
                type="button"
                @click="clearSingleFilter('invoiceNumberFilter')"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </th>
        <th>
          <div class="d-flex flex-row">
            <VDatePicker v-model.range="invoiceDateRange">
              <template #default="{ inputValue, inputEvents }">
                <div class="d-flex flex-row justify-content-center align-items-center">
                  <input
                      style="line-height: 2"
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      class="form-control text-center p-1"
                  />
                  <div class="text-nowrap px-1">-</div>
                  <input
                      style="line-height: 2"
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      class="form-control text-center p-1"
                  />
                </div>
              </template>
            </VDatePicker>
            <button
                v-if="invoiceDateRange?.start"
                type="button"
                class="btn btn-danger px-0 ms-1"
                @click="invoiceDateRange = { start: null, end: null }"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </th>
        <th>
          <div class="d-flex flex-row">
            <VDatePicker v-model.range="creationDateRange">
              <template #default="{ inputValue, inputEvents }">
                <div class="d-flex flex-row justify-content-center align-items-center">
                  <input
                      style="line-height: 2"
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      class="form-control text-center p-1"
                  />
                  <div class="text-nowrap px-1">-</div>
                  <input
                      style="line-height: 2"
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      class="form-control text-center p-1"
                  />
                </div>
              </template>
            </VDatePicker>
            <button
                v-if="creationDateRange?.start"
                type="button"
                class="btn btn-danger px-0 ms-1"
                @click="creationDateRange = { start: null, end: null }"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </th>
        <th>
          <div class="input-group input-group-sm" @keydown.enter.prevent="applyFilter">
            <input
                style="line-height: 2"
                type="text"
                class="form-control"
                v-model="buyerNameFilter"
                @blur="applyFilter"
            >
            <button
                v-if="buyerNameFilter"
                class="btn btn-danger"
                type="button"
                @click="clearSingleFilter('buyerNameFilter')"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </th>
        <th>
          <div class="input-group input-group-sm" @keydown.enter.prevent="applyFilter" >
            <SearchableDropdown
                input-id="zugferdVersionFilter"
                :model-value="zugferdVersionFilter"
                :options="masterDataStore.ZUGFeRDVersions"
                @update:modelValue="val => { zugferdVersionFilter = val; }"
                @apply-filters="applyFilter"
            />
          </div>
        </th>
        <th>
          <div class="input-group input-group-sm" @keydown.enter.prevent="applyFilter">
            <SearchableDropdown
                input-id="zugferdProfileFilter"
                :model-value="zugferdProfileFilter"
                :options="masterDataStore.ZUGFeRDProfiles"
                @update:modelValue="val => { zugferdProfileFilter = val; }"
                @apply-filters="applyFilter"
            />
          </div>
        </th>
        <th>
          <div class="input-group input-group-sm" @keydown.enter.prevent="applyFilter">
            <input
                style="line-height: 2"
                type="text"
                class="form-control"
                v-model="fileNameFilter"
                @blur="applyFilter"
            >
            <button
                v-if="fileNameFilter"
                class="btn btn-danger"
                type="button"
                @click="clearSingleFilter('fileNameFilter')"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </th>
      </tr>
      </thead>
      <tbody
          v-if="invoices.length > 0"
      >
      <tr
          v-for="invoice in invoices"
          :key="invoice.id"
          @click="selectInvoice(invoice)"
          :class="{ 'table-active': invoice === selectedInvoice }"
          style="cursor: pointer"
          @dblclick="$emit('edit')"
      >
        <td>{{ invoice.invoiceNumber }}</td>
        <td>{{ formatTimeStamp(invoice.invoiceDate) }}</td>
        <td>{{ formatTimeStamp(invoice.creationTimestamp) }}</td>
        <td>{{ invoice.buyerName }}</td>
        <td>{{ getVersionText(invoice.zugFeRDVersionId) }}</td>
        <td>{{ getProfileText(invoice.zugFeRDProfileId) }}</td>
        <td>{{ invoice.fileName }}</td>
      </tr>
      </tbody>
      <tbody v-else>
      <tr>
        <td colspan="7" class="text-center" style="height: 200px; vertical-align: middle;">
          {{ $t('table.noResults') }}
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import AppPagination from "../Shared/AppPagination.vue";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";
import {useMasterDataStore} from "@/stores/masterDataStore";

export default {
  name: "InvoiceTable",
  components: {SearchableDropdown, AppPagination},
  props: {
    invoices: Array,
    currentPage: Number,
    maxInvoices: Number,
    pageCount: Number,
    maxCount: Number,
    orderColumn: String,
    orderDirection: String
  },
  data() {
    return {
      selectedInvoice: null,
      invoiceNumberFilter: "",
      buyerNameFilter: "",
      fileNameFilter: "",
      invoiceDateRange: {
        start: null,
        end: null,
      },
      creationDateRange: {
        start: null,
        end: null,
      },
      zugferdVersionFilter: "",
      zugferdProfileFilter: "",
      searchTerm: "",
      zugferdVersionMapping: {
        Version1: "V1.0",
        Version20: "V2.0",
        Version23: "V2.3"
      },
      masterDataStore: useMasterDataStore(),
    };
  },
  watch: {
    invoiceDateRange: {
      handler(newRange) {
        if (newRange) {
          if (newRange.start)
          this.applyFilter();
        } else {
          //somehow it gets set to null from VCalendar so we need to reset it
          this.invoiceDateRange = {
            start: null,
            end: null
          };
          this.applyFilter();
        }
      }
    },
    creationDateRange: {
      handler(newRange) {
        if (newRange) {
          if (newRange.start)
          this.applyFilter();
        } else {
          //somehow it gets set to null from VCalendar so we need to reset it
          this.creationDateRange = {
            start: null,
            end: null
          };
          this.applyFilter();
        }
      }
    }
  },
  methods: {
    selectInvoice(invoice) {
      if (invoice) {
        this.selectedInvoice = invoice;
        this.$emit("select", invoice);
      }
    },
    setPage(page) {
      this.$emit("set-page", page);
    },
    setMax(max) {
      this.$emit("set-max", max);
    },
    getZugferdVersionDisplay(version) {
      return this.zugferdVersionMapping[version] || version;
    },
    applyFilter() {
      const invoiceNumbers = this.invoiceNumberFilter
          ? this.invoiceNumberFilter.split(',').map(v => v.trim()).filter(v => v)
          : [];
      const buyerNames = this.buyerNameFilter
          ? this.buyerNameFilter.split(',').map(v => v.trim()).filter(v => v)
          : [];
      const fileNames = this.fileNameFilter
          ? this.fileNameFilter.split(',').map(v => v.trim()).filter(v => v)
          : [];

      // Clone invoiceDateRange to avoid modifying the model directly
      let invoiceDateRange = this.invoiceDateRange;
      if (invoiceDateRange?.end) {
        const adjustedEnd = new Date(invoiceDateRange.end);
        adjustedEnd.setHours(23, 59, 59, 999);
        invoiceDateRange = {
          start: invoiceDateRange.start,
          end: adjustedEnd.toISOString()
        };
      }

      // If similar behavior is needed for creationDateRange, you can adjust it similarly
      let creationDateRange = this.creationDateRange;
      if (creationDateRange?.end) {
        const adjustedEnd = new Date(creationDateRange.end);
        adjustedEnd.setHours(23, 59, 59, 999);
        creationDateRange = {
          start: creationDateRange.start,
          end: adjustedEnd.toISOString()
        };
      }

      const zugferdVersion = this.zugferdVersionFilter;
      const zugferdProfile = this.zugferdProfileFilter;

      this.$emit("apply-filters", {
        invoiceNumbers,
        buyerNames,
        fileNames,
        invoiceDateRange,
        creationDateRange,
        zugferdVersion,
        zugferdProfile,
        searchTerm: this.searchTerm
      });
    },
    clearFilter() {
      this.invoiceNumberFilter = "";
      this.buyerNameFilter = "";
      this.zugferdVersionFilter = "";
      this.zugferdProfileFilter = "";
      this.fileNameFilter = "";
      this.invoiceDateRange = {
        start: null,
        end: null
      };
      this.creationDateRange = {
        start: null,
        end: null
      };
      this.searchTerm = "";
      this.$emit("clear-filters");
    },
    clearSingleFilter(field) {
      this[field] = "";
      this.applyFilter();
    },
    changeSort(column) {
      this.$emit('sort-changed', {column});
    },
    getSortIconClass(column) {
      if (this.orderColumn === column) {
        return this.orderDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
      }
      return 'bi-chevron-expand';
    },
    formatTimeStamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const datePart = date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const timePart = date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${datePart}, ${timePart}`;
    },
    getProfileText(id) {
      return this.masterDataStore.ZUGFeRDProfiles.find(p => p.id === id)?.name || id;
    },
    getVersionText(id) {
      return this.masterDataStore.ZUGFeRDVersions.find(p => p.id === id)?.name || id;
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
